import { getTierProduct, useQueryProducts } from '@/hooks/query/useQueryProducts';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  EndPromotedModalContent,
  EndPromotedModalHeader,
  useTrialTimer,
} from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { endPromotedTrialModalAtom } from '@heidi/commerce/data';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { memo, useState } from 'react';
import { usePromotedTrialManagement } from './hooks/usePromotedTrialManagement';

function EndPromotedTrialModalComponent() {
  const [period, setPeriod] = useState<Period>(Period.ONE_YEAR);
  const [open, setOpen] = useAtom(endPromotedTrialModalAtom);
  const { data: products } = useQueryProducts();
  const product = getTierProduct(SubscriptionTier.PRO, products);

  const handleClose = () => setOpen(false);

  const {
    billingInfo,
    handleStayPro,
    handleGoFree,
    isCreatingCheckout,
    isDeactivateTrialPending,
  } = usePromotedTrialManagement({ period, afterAction: handleClose });

  useTrialTimer({
    trialEndDate: billingInfo?.trialEndDate ?? undefined,
    isInTrial: billingInfo?.isInPromotedFreeTrial,
    onTrialExpired: () => setOpen(true),
  });

  const onOpenChange = (newIsOpen: boolean) => {
    if (newIsOpen) {
      setOpen(true);
      return;
    }
    void handleGoFree();
  };

  if (isAppleCompliantBuild()) {
    return null;
  }

  if (!billingInfo?.isInPromotedFreeTrial) {
    return null;
  }

  return (
    <DialogDrawer open={open} onOpenChange={onOpenChange}>
      <DialogDrawerContent className="flex max-w-2xl flex-col items-center gap-8">
        <EndPromotedModalHeader period={period} onPeriodChange={setPeriod} />
        <EndPromotedModalContent
          period={period}
          product={product}
          onStayPro={() => void handleStayPro()}
          onGoFree={() => void handleGoFree()}
          loadingStayPro={isCreatingCheckout}
          loadingGoFree={isDeactivateTrialPending}
        />
      </DialogDrawerContent>
    </DialogDrawer>
  );
}

export const EndPromotedTrialModal = memo(EndPromotedTrialModalComponent);
